import { FC, useState } from 'react';
import styled from 'styled-components';
import Card from './ui/Card';
import ValueSlider from './ui/ValueSlider';
import { GoldButton } from './ui/Buttons';
import { ui } from './ui/tokens';

// The style guide copy says 100 - 300, but existing creations and the grid
// presets use canvases as small as 10 px, so the editor keeps 10 as the floor.
export const CANVAS_MIN = 10;
export const CANVAS_MAX = 300;
const DEFAULT_SIZE = 100;

interface CanvasSizeModalProps {
	open: boolean;
	onConfirm: (width: number, height: number) => void;
	step?: number;
	totalSteps?: number;
}

/** "Select canvas size" wizard card (style guide page 1 / R14 step 5). */
const CanvasSizeModal: FC<CanvasSizeModalProps> = ({ open, onConfirm, step, totalSteps }) => {
	const [width, setWidth] = useState(DEFAULT_SIZE);
	const [height, setHeight] = useState(DEFAULT_SIZE);

	// Preview rectangle keeps the chosen aspect ratio inside a 64px box
	const longest = Math.max(width, height);
	const previewW = Math.max(14, Math.round((width / longest) * 64));
	const previewH = Math.max(14, Math.round((height / longest) * 64));

	return (
		<Card
			open={open}
			title="Select canvas size"
			step={step}
			totalSteps={totalSteps}
			description={`Choose the dimensions for your pixel art canvas. Limited to between ${CANVAS_MIN} - ${CANVAS_MAX} pixels.`}
		>
			<Dimensions aria-live="polite">
				{width} x {height} pixels
			</Dimensions>

			<PreviewBox>
				<Preview style={{ width: previewW, height: previewH }} aria-hidden />
			</PreviewBox>

			<ValueSlider label="Width" value={width} min={CANVAS_MIN} max={CANVAS_MAX} onChange={setWidth} editable />
			<ValueSlider label="Height" value={height} min={CANVAS_MIN} max={CANVAS_MAX} onChange={setHeight} editable />

			<GoldButton type="button" onClick={() => onConfirm(width, height)} style={{ width: '100%', marginTop: 28 }}>
				Create canvas
			</GoldButton>
		</Card>
	);
};

export default CanvasSizeModal;

const Dimensions = styled.div`
	margin-top: 22px;
	text-align: center;
	font-family: ${ui.font};
	font-size: 20px;
	font-weight: 600;
`;

const PreviewBox = styled.div`
	height: 84px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 10px 0 6px;
`;

const Preview = styled.div`
	background-color: ${ui.gold};
	border-radius: 2px;
	transition: width 0.15s ease, height 0.15s ease;
`;
